// The beam order of the Beacon on the card (numbers.md §8, R2858;
// ui-map-interface.md §8 "The beam order on the card").
//
// A SET Beacon beams while its beam order runs. Every SET starts the order,
// and it ends after `Catalog.beacon_beam_sec` of map time. The Beacon then
// stays SET and beams nothing, and the BEAM press (or the scan key `s`)
// starts the order again. The row takes the layout of the signature order
// row (`approach.ts`): the press with the order word, and beside it the
// state: ● ON with the time left, ○ OFF, or NEEDS SET while the anchor is
// not SET. The server decides every start; the module is pure.

import { AnchorState } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import { signatureRowHtml } from './approach';
import type { Action } from './card';

/** The act of the BEAM press: one `FLEET_OP_BEAM` for the hull. */
export const BEAM_ACT = 'beam';

/** The fields of a hull the beam row reads. */
export interface BeamFields {
  anchor: AnchorState;
  /** The map seconds of the beam order left, 0 while the beam is off. */
  beamLeftSec: number;
}

/** The BEAM press would take a press now: the anchor is SET and the beam
 *  is off. The key `s` reads the same gate as the press. */
export function beamReady(f: BeamFields): boolean {
  return f.anchor === AnchorState.ANCHOR_STATE_SET && f.beamLeftSec === 0;
}

/** The BEAM press: lit while the beam runs, and off unless `beamReady`.
 *  `dur` is the full beam order as the card prints a time, for the title. */
export function beamAction(fleetId: number, f: BeamFields, dur: string): Action {
  return {
    act: BEAM_ACT, id: fleetId, face: 'quiet', label: S.o_beam,
    title: dur ? S.beam_title({ dur }) : undefined,
    on: f.beamLeftSec > 0, off: !beamReady(f),
  };
}

/** The state beside the press: ● ON with `left`, the beam time left as the
 *  card prints it, ○ OFF, or NEEDS SET while the anchor is not SET. */
export function beamState(f: BeamFields, left: string): string {
  if (f.anchor !== AnchorState.ANCHOR_STATE_SET) return S.beam_needs_set;
  return f.beamLeftSec > 0 ? S.beam_on({ left }) : S.beam_off;
}

/** The row of the beam order, in the layout of the signature order row. */
export function beamRowHtml(action: Action, state: string): string {
  return signatureRowHtml(action, state, '');
}
